// src/store/aitime/aitimeSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../services/api.service';
import { errorMessage } from '../errorMessage';

// Async thunks
export const fetchProvisionings = createAsyncThunk(
  'aitime/fetchProvisionings',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await apiService.getProvisionings(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const fetchProvisioningById = createAsyncThunk(
  'aitime/fetchProvisioningById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiService.getProvisioning(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const createProvisioning = createAsyncThunk(
  'aitime/createProvisioning',
  async (data, { rejectWithValue }) => {
    try {
      const response = await apiService.createProvisioning(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  }
);

export const updateProvisioning = createAsyncThunk(
  'aitime/updateProvisioning',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await apiService.updateProvisioning(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  }
);

export const deleteProvisioning = createAsyncThunk(
  'aitime/deleteProvisioning',
  async (id, { rejectWithValue }) => {
    try {
      await apiService.deleteProvisioning(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const deactivateProvisioning = createAsyncThunk(
  'aitime/deactivateProvisioning',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiService.deactivateProvisioning(id);
      return { id, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const restoreProvisioning = createAsyncThunk(
  'aitime/restoreProvisioning',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiService.restoreProvisioning(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.response?.data?.detail || error.message);
    }
  }
);

export const regenerateLicense = createAsyncThunk(
  'aitime/regenerateLicense',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiService.regenerateLicense(id);
      return { id, license_key: response.data.license_key };
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const convertDemo = createAsyncThunk(
  'aitime/convertDemo',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await apiService.convertDemo(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  }
);

export const fetchDeploymentOptions = createAsyncThunk(
  'aitime/fetchDeploymentOptions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getDeploymentOptions();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const updateProvisioningStatus = createAsyncThunk(
  'aitime/updateProvisioningStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await apiService.updateProvisioningStatus(id, status);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.response?.data?.detail || error.message);
    }
  }
);

// Initial state
const initialState = {
  provisionings: [],
  currentProvisioning: null,
  deploymentOptions: {},
  isLoading: false,
  error: null,
  successMessage: null
};

// Create slice
const aitimeSlice = createSlice({
  name: 'aitime',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    clearCurrentProvisioning: (state) => {
      state.currentProvisioning = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchProvisionings
      .addCase(fetchProvisionings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProvisionings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.provisionings = action.payload;
      })
      .addCase(fetchProvisionings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // fetchProvisioningById
      .addCase(fetchProvisioningById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProvisioningById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentProvisioning = action.payload;
      })
      .addCase(fetchProvisioningById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // createProvisioning
      .addCase(createProvisioning.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createProvisioning.fulfilled, (state, action) => {
        state.isLoading = false;
        state.provisionings.push(action.payload);
        state.successMessage = 'AITIME provisioning created successfully';
      })
      .addCase(createProvisioning.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // updateProvisioning
      .addCase(updateProvisioning.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateProvisioning.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.provisionings.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.provisionings[index] = action.payload;
        }
        state.currentProvisioning = action.payload;
        state.successMessage = 'AITIME provisioning updated successfully';
      })
      .addCase(updateProvisioning.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // deleteProvisioning
      .addCase(deleteProvisioning.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteProvisioning.fulfilled, (state, action) => {
        state.isLoading = false;
        state.provisionings = state.provisionings.filter(p => p.id !== action.payload);
        if (state.currentProvisioning && state.currentProvisioning.id === action.payload) {
          state.currentProvisioning = null;
        }
        state.successMessage = 'AITIME provisioning deleted successfully';
      })
      .addCase(deleteProvisioning.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // deactivateProvisioning
      .addCase(deactivateProvisioning.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deactivateProvisioning.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.provisionings.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.provisionings[index].is_active = false;
        }
        if (state.currentProvisioning && state.currentProvisioning.id === action.payload.id) {
          state.currentProvisioning.is_active = false;
        }
        state.successMessage = 'AITIME provisioning deactivated successfully';
      })
      .addCase(deactivateProvisioning.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // restoreProvisioning
      .addCase(restoreProvisioning.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(restoreProvisioning.fulfilled, (state, action) => {
        state.isLoading = false;
        // Restored records leave whatever is_deleted-filtered list is currently shown.
        state.provisionings = state.provisionings.filter(p => p.id !== action.payload.id);
        if (state.currentProvisioning && state.currentProvisioning.id === action.payload.id) {
          state.currentProvisioning = action.payload;
        }
        state.successMessage = 'AITIME provisioning restored successfully';
      })
      .addCase(restoreProvisioning.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // regenerateLicense
      .addCase(regenerateLicense.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(regenerateLicense.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.provisionings.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.provisionings[index].license_key = action.payload.license_key;
        }
        if (state.currentProvisioning && state.currentProvisioning.id === action.payload.id) {
          state.currentProvisioning.license_key = action.payload.license_key;
        }
        state.successMessage = 'License key regenerated successfully';
      })
      .addCase(regenerateLicense.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // convertDemo
      .addCase(convertDemo.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(convertDemo.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.provisionings.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.provisionings[index] = action.payload;
        }
        if (state.currentProvisioning && state.currentProvisioning.id === action.payload.id) {
          state.currentProvisioning = action.payload;
        }
        state.successMessage = 'Provisioning converted to a paid license successfully';
      })
      .addCase(convertDemo.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // fetchDeploymentOptions
      .addCase(fetchDeploymentOptions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDeploymentOptions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.deploymentOptions = action.payload;
      })
      .addCase(fetchDeploymentOptions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // updateProvisioningStatus
      .addCase(updateProvisioningStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateProvisioningStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.provisionings.findIndex(p => p.id === action.payload.data?.id);
        if (index !== -1) {
          state.provisionings[index] = action.payload.data;
        }
        if (state.currentProvisioning && state.currentProvisioning.id === action.payload.data?.id) {
          state.currentProvisioning = action.payload.data;
        }
        state.successMessage = action.payload.message || 'Status updated successfully';
      })
      .addCase(updateProvisioningStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError, clearSuccessMessage, clearCurrentProvisioning } = aitimeSlice.actions;
export default aitimeSlice.reducer;